/**
 * @fileoverview Import CMS Data Dialog - Accepts pasted JSON to create the import artifact.
 *
 * Validates the pasted JSON against the import payload schema,
 * creates the import artifact via the API, and reports success/failure.
 */
import { AlertCircle, FileUp, Loader2 } from 'lucide-react';
import React, { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useCreateCaseIngestionImportMutation } from '@/services/api/caseIngestionAssistService';
import type { CreateCaseIngestionImportRequest } from '@/services/types/case-ingestion-assist-types';
import { CreateImportRequestSchema } from '@/services/types/case-ingestion-assist-types';
import { getApiErrorMessage } from '@/utils/api-error';

interface ImportCmsDataDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  matterId: string | null;
  onImportSuccess: () => void;
  /** Pre-creation mode: store validated import in context instead of POSTing */
  onPendingImport?: (data: CreateCaseIngestionImportRequest) => void;
}

export const ImportCmsDataDialog: React.FC<ImportCmsDataDialogProps> = ({
  open,
  onOpenChange,
  matterId,
  onImportSuccess,
  onPendingImport,
}) => {
  const [jsonInput, setJsonInput] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const [createImport, { isLoading }] = useCreateCaseIngestionImportMutation();

  const handleImport = useCallback(async () => {
    setValidationError(null);

    // Parse JSON
    let parsed: unknown;
    try {
      parsed = JSON.parse(jsonInput);
    } catch {
      setValidationError('Invalid JSON. Please check the format and try again.');
      return;
    }

    // Validate against schema
    const result = CreateImportRequestSchema.safeParse(parsed);
    if (!result.success) {
      const firstError = result.error.issues[0];
      setValidationError(
        `Validation error at ${firstError?.path.join('.')}: ${firstError?.message}`,
      );
      return;
    }

    // Pre-creation mode: store in context
    if (onPendingImport) {
      onPendingImport(result.data);
      toast.success('CMS data loaded — fields will auto-fill');
      setJsonInput('');
      onOpenChange(false);
      onImportSuccess();
      return;
    }

    // Post-creation mode: POST to API
    if (matterId) {
      try {
        await createImport({ matterId, data: result.data }).unwrap();
        toast.success('CMS data imported successfully');
        setJsonInput('');
        onOpenChange(false);
        onImportSuccess();
      } catch (error) {
        toast.error(getApiErrorMessage(error, 'Failed to import CMS data'));
      }
    }
  }, [jsonInput, matterId, createImport, onOpenChange, onImportSuccess, onPendingImport]);

  const handleClose = useCallback(() => {
    if (!isLoading) {
      setJsonInput('');
      setValidationError(null);
      onOpenChange(false);
    }
  }, [isLoading, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-sm">
            <FileUp className="h-4 w-4" />
            Import CMS JSON
          </DialogTitle>
          <DialogDescription className="text-xs">
            Paste the JSON export from your CMS to auto-fill case information.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <Textarea
            value={jsonInput}
            onChange={(e) => {
              setJsonInput(e.target.value);
              setValidationError(null);
            }}
            placeholder='Paste CMS JSON here...'
            className="min-h-[200px] font-mono text-xs"
            disabled={isLoading}
          />

          {validationError && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 p-2 text-xs text-red-700 dark:bg-red-950/30 dark:text-red-400">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
              <span>{validationError}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleImport}
            disabled={!jsonInput.trim() || isLoading}
          >
            {isLoading && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            Import
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
